import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import surveyService from '../services/surveyService';
import {
  Button,
  CircularProgress,
  Alert,
  Typography,
  Chip,
  Box,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';

const SurveyResponsesPage = () => {
  const { surveyId } = useParams();
  const navigate = useNavigate();
  
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // New States for Search and Details Modal
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedResponse, setSelectedResponse] = useState(null);

  useEffect(() => {
    const fetchResponses = async () => {
      try {
        const data = await surveyService.getResponsesBySurveyId(surveyId);
        setResponses(data);
      } catch (err) {
        console.error('Error fetching responses:', err);
        setError(err.message || 'Failed to load responses');
      } finally {
        setLoading(false);
      }
    };

    fetchResponses();
  }, [surveyId]);

  // Filter logic matching customer details
  const filteredResponses = responses.filter(r => {
    const name = r.customerName?.toLowerCase() || '';
    const email = r.customerEmail?.toLowerCase() || '';
    const query = searchQuery.toLowerCase();
    return name.includes(query) || email.includes(query);
  });

  const getPhotosCount = (answers) => {
    return answers.filter(ans => 
      ans.answerText && ans.answerText.startsWith('https://storage.googleapis.com/')
    ).length;
  };

  const renderAnswer = (answer) => {
    if (answer.answerText && answer.answerText.startsWith('https://storage.googleapis.com/')) {
      return (
        <Box sx={{ mt: 1 }}>
          <img
            src={answer.answerText}
            alt="Survey response"
            style={{
              maxWidth: '100%',
              maxHeight: '350px',
              borderRadius: '8px',
              border: '1px solid #e0e0e0',
              cursor: 'pointer',
            }}
            onClick={() => window.open(answer.answerText, '_blank')}
          />
        </Box>
      );
    }

    return (
      <Typography variant="body1" sx={{ mt: 0.5, color: '#333', fontWeight: 500 }}>
        {answer.answerText || 'No answer provided'}
      </Typography>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
        <CircularProgress sx={{ color: '#a8851d' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/dashboard/survey-management')}
          sx={{ mt: 2, color: '#a8851d' }}
        >
          Back to Surveys
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Panel */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2, mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/dashboard/survey-management')}
            sx={{ mr: 2, color: '#a8851d' }}
          >
            Back
          </Button>
          <Box>
            <Typography variant="h4" sx={{ color: '#1a1a1a', fontWeight: 600 }}>
              Survey Responses
            </Typography>
            <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
              {responses.length} {responses.length === 1 ? 'submission' : 'submissions'} total
            </Typography>
          </Box>
        </Box>

        {/* Search Field */}
        {responses.length > 0 && (
          <TextField
            placeholder="Search by name or email..."
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ width: { xs: '100%', sm: 300 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#999' }} />
                </InputAdornment>
              ),
            }}
          />
        )}
      </Box>

      {/* Main Table Layout */}
      {responses.length === 0 ? (
        <Paper sx={{ p: 5, textAlign: 'center', bgcolor: '#f9f9f9', borderRadius: 2 }}>
          <Typography variant="h6" sx={{ color: '#666', mb: 1 }}>
            No responses yet
          </Typography>
          <Typography variant="body2" sx={{ color: '#999' }}>
            Share your survey link to start collecting submissions.
          </Typography>
        </Paper>
      ) : filteredResponses.length === 0 ? (
        <Paper sx={{ p: 5, textAlign: 'center', bgcolor: '#f9f9f9', borderRadius: 2 }}>
          <Typography variant="h6" sx={{ color: '#666' }}>
            No results match your search query
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
          <Table>
            <TableHead sx={{ bgcolor: '#f5f5f5' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', width: '60px' }}>#</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Customer Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Contact Info</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Submitted At</TableCell>
                <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Photos</TableCell>
                <TableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredResponses.map((response, index) => {
                const photosCount = getPhotosCount(response.answers);
                return (
                  <TableRow 
                    key={response.id}
                    sx={{ '&:hover': { bgcolor: '#fcfbf7' } }}
                  >
                    <TableCell>{responses.length - index}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#333' }}>
                      {response.customerName}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {response.customerEmail && (
                          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#555' }}>
                            <EmailIcon sx={{ fontSize: 14, color: '#a8851d' }} />
                            {response.customerEmail}
                          </Typography>
                        )}
                        {response.customerPhone && (
                          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#555' }}>
                            <PhoneIcon sx={{ fontSize: 14, color: '#a8851d' }} />
                            {response.customerPhone}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#666' }}>
                        <CalendarTodayIcon sx={{ fontSize: 14, color: '#999' }} />
                        {new Date(response.submittedAt).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {photosCount > 0 ? (
                        <Chip
                          icon={<PhotoCameraIcon sx={{ fontSize: '14px !important' }} />}
                          label={`${photosCount} Photo${photosCount > 1 ? 's' : ''}`}
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton 
                        color="primary"
                        onClick={() => setSelectedResponse(response)}
                        sx={{ color: '#a8851d', '&:hover': { bgcolor: '#fcf8ec' } }}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Response Details Pop-up Modal (MUI Dialog) */}
      <Dialog
        open={Boolean(selectedResponse)}
        onClose={() => setSelectedResponse(null)}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: { borderRadius: 2, borderTop: '6px solid #a8851d' }
        }}
      >
        {selectedResponse && (
          <>
            <DialogTitle sx={{ m: 0, p: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1a1a1a' }}>
                Response Details
              </Typography>
              <Chip
                icon={<CalendarTodayIcon sx={{ fontSize: 14 }} />}
                label={new Date(selectedResponse.submittedAt).toLocaleString()}
                size="small"
                sx={{ bgcolor: '#f5f5f5', fontWeight: 500 }}
              />
            </DialogTitle>
            
            <DialogContent dividers sx={{ p: 3 }}>
              {/* Customer Info Card Inside Modal */}
              <Box sx={{ bgcolor: '#f9f9f9', p: 2.5, borderRadius: 2, mb: 4, border: '1px solid #eee' }}>
                <Typography variant="subtitle2" sx={{ color: '#666', mb: 1.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Customer Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon sx={{ color: '#a8851d' }} />
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{selectedResponse.customerName}</Typography>
                  </Grid>
                  {selectedResponse.customerEmail && (
                    <Grid item xs={12} sm={4} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <EmailIcon sx={{ color: '#a8851d' }} />
                      <Typography variant="body1">{selectedResponse.customerEmail}</Typography>
                    </Grid>
                  )}
                  {selectedResponse.customerPhone && (
                    <Grid item xs={12} sm={4} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PhoneIcon sx={{ color: '#a8851d' }} />
                      <Typography variant="body1">{selectedResponse.customerPhone}</Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>

              {/* Individual Question Answers */}
              <Typography variant="subtitle2" sx={{ color: '#666', mb: 2.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Submission Responses
              </Typography>
              {selectedResponse.answers.map((answer, idx) => (
                <Box key={idx} sx={{ mb: 4 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1a1a1a', mb: 0.5 }}>
                    {answer.questionText}
                  </Typography>
                  <Chip
                    label={answer.questionType}
                    size="small"
                    sx={{ 
                      fontSize: '0.7rem', 
                      height: '20px', 
                      mb: 1.5,
                      bgcolor: '#e8f5e9',
                      color: '#2e7d32',
                      fontWeight: 600
                    }}
                  />
                  {renderAnswer(answer)}
                  {idx < selectedResponse.answers.length - 1 && <Divider sx={{ mt: 3, mb: 1 }} />}
                </Box>
              ))}
            </DialogContent>
            
            <DialogActions sx={{ p: 2 }}>
              <Button 
                onClick={() => setSelectedResponse(null)}
                variant="outlined"
                sx={{ borderColor: '#ccc', color: '#666', '&:hover': { borderColor: '#999', bgcolor: '#f5f5f5' } }}
              >
                Close Details
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default SurveyResponsesPage;